import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpHandlerFn, HttpInterceptor, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, timer, switchMap, catchError, throwError, map, from } from 'rxjs';
import { StorageService } from '../_services/storage.service';
import { AuthService } from '../_services/auth.service';
import { InactivityService } from './inactivity-check-service';
import { EncryptionService } from './encryption.service';
import { environment } from '../../environments/environment';
import Swal from 'sweetalert2';
import { AngularDeviceInformationService } from 'angular-device-information';
import { AuditApiCategory, auditApiCategories } from '../_helpers/audit.apiCategorize';

export const httpRequestInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  // console.debug("Inside Interceptor url: ",req.url);

  const storageService = inject(StorageService);
  const authService = inject(AuthService);
  const inactivityService = inject(InactivityService);
  const encryptionService = inject(EncryptionService);
  const router = inject(Router);
  const deviceService = inject(AngularDeviceInformationService);

  const token = storageService.getToken();
  inactivityService.saveActivity();

  let lastRefreshTime = 0;
  let isRefreshing = false;

  // 🚫 Define APIs that should NOT get Bearer token
  const excludedTokenUrls = [
    '/user-management/auth/encrypt/login',
    '/user-management/auth/public-key',
    '/auth/api-credential/token',
    '/auth/api-credential/refresh',

    // PG Endpoints 
    '/pgws/wallet/validate',
    '/pgws/wallet/collectpay',
    '/pgws/transact',
    '/pgws/refund/requery',
    '/pgws/merchant/refund',
    '/crmws/shield-api/crm-merchant-setup/merchant-credentials'

  ];

  const encryptByPublicKeyUrls: string[] = [];

  // 🚫 Define APIs that should NOT be encrypted
  const encryptionUrls: string[] = [];

  const ignoreDecryptUrls: string[] = [];

  // Define APIs that should have X-ENV header
  const xEnvEndpoints = [
  '/developer-portal/profile',
  'user-management/developer-portal/users'
  ];

  let isSessionTerminationEventSent = false
  const DEDUP_WINDOW_MS = 1000; // 1 second window for deduplication

  // ✅ Check if current request should be excluded
  const reqPath = req.url.split('?')[0];
  const isAuditLogging = getCategoryByUrl(req.url) != null;
  const skipAuth = excludedTokenUrls.some(url => reqPath.endsWith(url)) || reqPath.includes('/merchant-portal/payout/dp') || reqPath.includes('/developer-portal/payment-gateway');
  const needToEncrypt = encryptionUrls.some(url => reqPath.endsWith(url));
  const isEncryptByPublicKey = encryptByPublicKeyUrls.some(url => reqPath.endsWith(url));
  const needToDecrypt = encryptionUrls.some(url => reqPath.endsWith(url)) && !ignoreDecryptUrls.some(url => reqPath.endsWith(url));

  /**
   * API Explorer calls (e.g. token, refresh, payment gateway) may legitimately return 401.
   * These must never trigger a session logout, errors should be shown as is in the UI.
   */
  const isApiExplorerCall = req.url.includes('/auth/api-credential/') || req.url.includes('/merchant-portal/payout/dp/');

  if (token && storageService.isJwtExpired() && !req.url.endsWith('/auth/refresh') && canRefreshToken()) {
    console.log("Refreshing token");
    isRefreshing = true;

    authService.refreshAccessToken(storageService.getUser()).subscribe((response: any) => {
      storageService.saveUserData(response);
      isRefreshing = false;
    });
  }

  if (!req.url.endsWith('/auth/refresh') && isRefreshing) {
    console.log("waiting 2 seconds");
    return timer(200).pipe(
      switchMap(() => from(processRequestAsync(req, token, skipAuth, needToEncrypt, isEncryptByPublicKey))),
      switchMap((processedRequest) => next(processedRequest))
    );
  }

  // Handle async encryption
  return from(processRequestAsync(req, token, skipAuth, needToEncrypt, isEncryptByPublicKey)).pipe(
    switchMap((processedRequest) => next(processedRequest)),
    switchMap((event: HttpEvent<any>) => {
      if (isAuditLogging && req.method !== "OPTIONS" && !isDuplicatedSessionTerminationEventSent()) {
        logAuditActivity(req, token);
      }
      if (event instanceof HttpResponse) {
        // Check if need to decrypt response body
        if (needToDecrypt && event.body) {
          // console.debug("Decryption needed for URL: ", req.url);
          return from(encryptionService.decryptResponseBody(event.body)).pipe(
            map(decryptedBody => {
              // Wrap decrypted data in response property to maintain original structure
              return event.clone({ body: decryptedBody });
            }),
            catchError(error => {
              console.warn('Failed to decrypt response, returning original:', error);
              Swal.fire("Logout due to duplicated login from other devices", '', 'error');
              authService.logout();
              return of(event);
            })
          );
        }
      }
      return of(event);
    }),
    catchError((error) => {
      /**
       * Only handle 401 errors originating from the Developer Portal backend.
       * API Explorer makes direct calls to external APIs (e.g. Generate Token) which may
       * legitimately return 401, those should NOT trigger a session logout.
       */
      if (error.status === 401  && !isApiExplorerCall && (
        req.url.startsWith(environment.AUTHORIZER_URL)
        || req.url.startsWith(environment.DEVELOPER_PORTAL_URL)
        || req.url.startsWith(environment.USER_MANAGEMENT_URL)
        || req.url.startsWith(environment.AUDIT_URL)
        )) {
        console.warn('401 UNAUTHORIZED received — clearing session and redirecting to login:', req.url);
        storageService.clean();
        router.navigate(['/auth/login']);
        throw error;
      }
      console.error(error);
      return throwError(() => error);
    })
  );

  async function processRequestAsync(request: HttpRequest<any>, token: string | null, skipAuth: boolean, needToEncrypt: boolean, isEncryptByPublicKey: boolean): Promise<HttpRequest<any>> {
    // await encryptionService.registerEncKeyIfNotExist();
    let modifiedRequest = request;

    // check if need to encrypt request body
    // console.debug("skipAuth",skipAuth + ", needToEncrypt", needToEncrypt + ", isEncryptByPublicKey", isEncryptByPublicKey + ", request.url",request.url);

    if (needToEncrypt && request.body && (request.method === 'POST' || request.method === 'PUT' || request.method === 'PATCH')) {
      try {
        // console.debug("Encryption needed for URL: ", request.url);
        const encryptedBody = await encryptionService.encryptRequestBody(request.body, isEncryptByPublicKey, request.url.split('?')[0]);
        modifiedRequest = request.clone({
          body: encryptedBody,
          setHeaders: {
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.warn('Failed to encrypt request body, sending original:', error);
        Swal.fire("Logout due to duplicated login from other devices", '', 'error');
        authService.logout();
      }
    }

    if (skipAuth) {
      // console.debug('Skipping Bearer token for', modifiedRequest.url);
      return modifiedRequest; // 🚫 No Authorization header
    }
    return attachAuthHeader(modifiedRequest, token);
  }

  function attachAuthHeader(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    const headers: any = {};

    // Only add X-ENV header if request URL starts with DEVELOPER_PORTAL_URL
    if (request.url.startsWith(environment.DEVELOPER_PORTAL_URL)
      || xEnvEndpoints.some(endpoint => request.url.includes(endpoint))) {
      headers['X-ENV'] = localStorage.getItem('environment')?.toUpperCase() || 'SANDBOX';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return request.clone({ setHeaders: headers });
  }

  function canRefreshToken(): boolean {
    const currentTime = Date.now();
    if (currentTime - lastRefreshTime < 60000) return false;
    lastRefreshTime = currentTime;
    return true;
  }

  function logAuditActivity(req: HttpRequest<any>, token: string | null): void {
    try {
      // Use setTimeout to make this non-blocking
      setTimeout(() => {
        performAuditLog(req, token);
      }, 0);
    } catch (error) {
      console.error('Error in audit logging:', error);
    }
  }

  function performAuditLog(req: HttpRequest<any>, token: string | null): void {
    const deviceInfo = JSON.parse(JSON.stringify(deviceService.getDeviceInfo()));

    // Get API category info with fallback
    let apiInfo = null;
    try {
      apiInfo = getCategoryByUrl(req.url);
    } catch (error) {
      console.error('Error getting API category:', error);
    }

    const currentTime = Date.now();

    const auditPayload = {
      // Original data
      ...deviceInfo,
      uuid: localStorage.getItem("userKey"),
      url: req.url,
      method: req.method,
      user: localStorage.getItem("emailId"),
      accessedBy: 'Developer_Portal',

      // API categorization - Use actual page category, not PII_AUDIT
      category: apiInfo?.category,
      action: apiInfo?.action,
      flowType: apiInfo?.flow || 'DEVELOPER_PORTAL_GENERAL',
      accessLevel: apiInfo?.accessLevel,
      dataFlow: apiInfo?.dataFlow,

      // Enhanced payload information for PII auditing
      payload: {
        body: sanitizeRequestBody(req.body),
        queryParams: extractQueryParams(req.url),
        pathParams: extractPathParams(req.url)
      },

      // Enhanced metrics for PII
      requestPayloadSize: req.body ? JSON.stringify(req.body).length : 0,
      hasAuthentication: !!token,
      isHighRiskOperation: true, // All PII operations are high risk

      // Timestamp
      timestamp: currentTime,
      createDate: new Date().toISOString()
    };

    // Store last call time for next calculation
    const payloadJson = JSON.stringify(auditPayload);
    console.log("🔒 Sending audit log via sendBeacon:", payloadJson);

    // Use sendBeacon for reliable delivery
    const success = window.navigator.sendBeacon(
      `${environment.AUDIT_URL}/shield-activity-log/save`,
      payloadJson
    );

    if (!success) {
      console.warn('⚠️ PII Audit SendBeacon failed, request may not have been sent');
    }
  }

  function getCategoryByUrl(url: string): AuditApiCategory | null {
    // Extract path from full URL if it contains domain
    let cleanUrl = url;

    // Remove protocol and domain if present
    if (url.includes('://')) {
      try {
        const urlObj = new URL(url);
        cleanUrl = urlObj.pathname;
      } catch (e) {
        // If URL parsing fails, use the original approach
        cleanUrl = url;
      }
    }

    // Remove leading slash if present
    cleanUrl = cleanUrl.startsWith('/') ? cleanUrl.substring(1) : cleanUrl;

    // Remove query parameters and fragments
    cleanUrl = cleanUrl.split('?')[0].split('#')[0];

    console.log(`Trying to categorize cleaned URL: ${cleanUrl}`);

    // 1. Direct exact match
    if (auditApiCategories[cleanUrl]) {
      console.log(`Direct match found for: ${cleanUrl}`);
      return auditApiCategories[cleanUrl];
    }

    // 2. Try with leading slash
    const slashUrl = '/' + cleanUrl;
    if (auditApiCategories[slashUrl]) {
      console.log(`Slash match found for: ${slashUrl}`);
      return auditApiCategories[slashUrl];
    }
    return null;
  }

  function sanitizeRequestBody(body: any): any {
    if (!body) return null;

    // Create a copy to avoid modifying the original
    const sanitized = { ...body };

    // For PII audit calls, preserve audit fields but remove sensitive data
    const sensitiveFields = ['password', 'currentPassword', 'newPassword', 'token', 'refresh_token', 'access_token', 'secret', 'key', 'credential'];
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    return { ...sanitized };
  }

  function extractQueryParams(url: string): { [key: string]: string } {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params: { [key: string]: string } = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    });

    return params;
  }

  function extractPathParams(url: string): string[] {
    return url.split('/').filter(segment => segment && !segment.includes('?'));
  }

  function isDuplicatedSessionTerminationEventSent(): boolean {
    if (isSessionTerminationEventSent) {
      return true;
    }
    isSessionTerminationEventSent = true;
    setTimeout(() => {
      isSessionTerminationEventSent = false;
    }, DEDUP_WINDOW_MS * 2);
    return false;
  }
};

export const HttpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useValue: httpRequestInterceptor, multi: true }
];